/**
 * contentEditable 富文本与纯文本之间的转换
 *
 * 可编辑单元格里的换行结构由浏览器决定，可能是 <ne-p>、<div>、<br>
 * 或裸文本节点的任意组合，所以不能靠 textContent（会压平换行），
 * 也不能只认某一种标签。这里按块级 / 内联 / <br> 的语义还原行。
 */

import { MODIFIER_HINT } from './linkClick'

// 内联标签不产生换行，其余标签一律按块级处理
const INLINE_TAGS = new Set([
    'a', 'todo', 'span', 'b', 'i', 'em', 'strong', 'u', 's', 'del', 'ins',
    'code', 'kbd', 'samp', 'var', 'small', 'mark', 'sub', 'sup', 'font',
    'label', 'abbr', 'cite', 'q', 'time', 'big', 'tt', 'nobr',
])

/** 判断 <br> 之后是否只剩空白文本，浏览器常用尾部 <br> 做占位而非真实换行 */
const isTrailingBr = (node) => {
    let next = node.nextSibling
    while (next) {
        if (next.nodeType === Node.TEXT_NODE) {
            if (next.nodeValue.trim() !== '') return false
        } else {
            return false
        }
        next = next.nextSibling
    }
    return true
}

/**
 * 把 DOM 元素的内容抽取为纯文本，块级元素与 <br> 转成 \n
 * @param {Element|null} element
 * @returns {string}
 */
export const elementToText = (element) => {
    if (!element) return ''

    const lines = ['']
    const appendText = (text) => {
        lines[lines.length - 1] += text
    }
    const newLine = () => {
        lines.push('')
    }

    const walk = (node) => {
        node.childNodes.forEach(child => {
            if (child.nodeType === Node.TEXT_NODE) {
                // HTML 源码里的换行只是排版，不代表内容换行
                appendText(child.nodeValue.replace(/[\r\n]+/g, ' '))
                return
            }
            if (child.nodeType !== Node.ELEMENT_NODE) return

            const tag = child.tagName.toLowerCase()

            if (tag === 'br') {
                if (!isTrailingBr(child)) newLine()
                return
            }
            if (INLINE_TAGS.has(tag)) {
                walk(child)
                return
            }

            // 块级元素：前后各自成行
            if (lines[lines.length - 1] !== '') newLine()
            walk(child)
            newLine()
        })
    }

    walk(element)
    return lines.join('\n').trim()
}

/**
 * 把纯文本渲染为可编辑单元格的 HTML，每行一个 <ne-p>
 * @param {string} text
 * @returns {string}
 */
export const formatText = (text) => {
    if (!text || text.trim() === '') {
        return '<ne-p><br></ne-p>'
    }
    return text.split('\n').map(line => {
        const trimmedLine = line.trim()
        if (trimmedLine === '') {
            return '<ne-p><br></ne-p>'
        }
        if (trimmedLine.startsWith('http')) {
            return `<ne-p><a href="${trimmedLine}" target="_blank" rel="noopener noreferrer" title="${MODIFIER_HINT}">${trimmedLine}</a></ne-p>`
        }
        if (trimmedLine.toLowerCase().startsWith('todo')) {
            return `<ne-p><todo>${line}</todo></ne-p>`
        }
        return `<ne-p>${line}</ne-p>`
    }).join('')
}
